/**
 * Notification utility functions for position state detection and navigation path generation
 * Used to determine correct navigation paths for notifications based on position state
 */

/**
 * Determine if a notification refers to an open or closed position
 */
export function detectPositionState(data: {
  notificationType: string;
  symbol?: string;
  account?: string;
  side?: string;
  tradeId?: string | number;
  metadata?: any;
}): {
  positionStatus: 'open' | 'closed';
  isLivePosition: boolean;
  shouldNavigateToChart: boolean;
  targetBasePath: '/live-trades' | '/closed-trades';
} {
  const { notificationType, tradeId } = data;
  
  // Position closed and trade closed always refer to closed positions
  if (notificationType === 'position_closed' || notificationType === 'trade_closed') {
    return {
      positionStatus: 'closed',
      isLivePosition: false,
      shouldNavigateToChart: true,
      targetBasePath: '/closed-trades'
    };
  }
  
  // Position opened, modified, milestone (without tradeId) refer to open positions
  if (['position_opened', 'position_modified', 'milestone'].includes(notificationType)) {
    // Special case: milestone with tradeId indicates closed position milestone
    if (notificationType === 'milestone' && tradeId) {
      return {
        positionStatus: 'closed',
        isLivePosition: false,
        shouldNavigateToChart: true,
        targetBasePath: '/closed-trades'
      };
    }
    
    return {
      positionStatus: 'open',
      isLivePosition: true,
      shouldNavigateToChart: true,
      targetBasePath: '/live-trades'
    };
  }
  
  // Default to open position for unknown types
  return {
    positionStatus: 'open',
    isLivePosition: true,
    shouldNavigateToChart: false,
    targetBasePath: '/live-trades'
  };
}

/**
 * Generate navigation path based on position state and available data
 */
export function generateNavigationPath(data: {
  positionStatus: 'open' | 'closed';
  symbol: string;
  account: string;
  side?: string;
  tradeId?: string | number;
}): {
  fullPath: string;
  fallbackPath: string;
  state: {
    from: string;
    context: string;
  };
} {
  const { positionStatus, symbol, account, side, tradeId } = data;
  
  const encodedSymbol = encodeURIComponent(symbol);
  const encodedAccount = encodeURIComponent(account);
  
  if (positionStatus === 'open') {
    if (side) {
      // Complete data available - navigate to specific chart view
      const encodedSide = encodeURIComponent(side);
      return {
        fullPath: `/live-trades/chart/${encodedSymbol}/${encodedSide}/${encodedAccount}`,
        fallbackPath: `/live-trades?filter=${encodedSymbol}&account=${encodedAccount}`,
        state: {
          from: '/live-trades',
          context: 'notification'
        }
      };
    } else {
      // Missing side data - use filtered list as primary path (chart would fail anyway)
      console.warn(`🔧 [Navigation] Missing 'side' parameter for open position notification: ${symbol}/${account}`);
      return {
        fullPath: `/live-trades?filter=${encodedSymbol}&account=${encodedAccount}`,
        fallbackPath: `/live-trades`,
        state: {
          from: '/live-trades',
          context: 'notification'
        }
      };
    }
  }
  
  if (positionStatus === 'closed' && tradeId) {
    return {
      fullPath: `/closed-trades/chart/${encodedSymbol}/${encodedAccount}/${tradeId}`,
      fallbackPath: `/closed-trades?filter=${encodedSymbol}&account=${encodedAccount}`,
      state: {
        from: '/closed-trades', 
        context: 'notification'
      }
    };
  }
  
  // Final fallback for any remaining cases (closed positions without tradeId)
  const basePath = positionStatus === 'closed' ? '/closed-trades' : '/live-trades';
  return {
    fullPath: `${basePath}?filter=${encodedSymbol}&account=${encodedAccount}`,
    fallbackPath: basePath,
    state: {
      from: basePath,
      context: 'notification'
    }
  };
}